import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { unitService } from '../../../services/unitService';
import { useAuth } from '../../../contexts/AuthContext';

const TrainerAssignmentModal = ({ isOpen, onClose, onSave, unit }) => {
  const { user } = useAuth();
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTrainers();
      if (unit?.trainerAssignment?.trainer?.id) {
        setSelectedTrainerId(unit?.trainerAssignment?.trainer?.id);
      } else {
        setSelectedTrainerId('');
      }
      setError(null);
    }
  }, [isOpen, unit]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // ... other states

  // Filtered trainers for dropdown with safe access
  const filteredTrainers = (trainers || []).filter(t => {
    if (!t) return false;

    // Check workload limits for external trainers
    const trainerDeptId = t.department?.id || t.department_id;
    const isHomeDept = trainerDeptId === unit?.program?.department?.id;
    
    const isAtUnitLimit = t.max_workload_units !== null && t.max_workload_units !== undefined && t.current_units >= t.max_workload_units;
    const isAtHourLimit = t.max_workload_hours !== null && t.max_workload_hours !== undefined && t.current_hours >= t.max_workload_hours;
    
    // Hide trainers if they have reached either workload limit
    if (isAtUnitLimit || isAtHourLimit) {
      return false;
    }

    const name = (t.full_name || t.name || '').trim();
    const email = (t.institutional_email || t.email || '').trim();
    const pfNumber = (t.employee_id || '').trim();
    const role = (t.job_title || t.primary_role || '').trim();
    const searchLow = (searchTerm || '').toLowerCase();

    // Allow if name OR email matches, but ensure at least one identifier exists
    if (!name && !email && !pfNumber) return false;

    return [name, email, pfNumber, role].some(value => value.toLowerCase().includes(searchLow));
  });

  // Effect to sync search term when selectedTrainerId changes (e.g. initial load)
  useEffect(() => {
    if (selectedTrainerId && trainers && trainers.length > 0) {
      const selected = trainers.find(t => t && t.id === selectedTrainerId);
      if (selected) {
        setSearchTerm(selected.full_name || selected.name || '');
      }
    } else if (!selectedTrainerId) {
      setSearchTerm('');
    }
  }, [selectedTrainerId, trainers]);

  const fetchTrainers = async () => {
    try {
      setLoadingTrainers(true);
      setError(null);
      // This intentionally returns active trainers from every department. The
      // database routes external allocations to the trainer's mother HOD.
      const data = await unitService.getAssignableTrainers();
      setTrainers(data);
      if (!data.length) {
        setError('No active academic staff were found. Confirm the staff account is active and has an academic role.');
      }
    } catch (err) {
      console.error('Error fetching trainers:', err);
      setTrainers([]);
      setError(err?.message || 'Unable to load trainers. Please retry.');
    } finally {
      setLoadingTrainers(false);
    }
  };

  const handleTrainerSelect = (trainer) => {
    setSelectedTrainerId(trainer.id);
    setSearchTerm(trainer.full_name || trainer.name || '');
    setIsDropdownOpen(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    // ... validation starts here
    if (!selectedTrainerId) {
      setError('Please select a trainer');
      return;
    }

    const selectedTrainer = trainers.find(t => t.id === selectedTrainerId);

    try {
      setLoading(true);
      setError(null);
      await onSave({
        unitId: unit?.id,
        trainerId: selectedTrainerId,
        unitDeptId: unit?.program?.department?.id,
        trainerDeptId: selectedTrainer?.department?.id || selectedTrainer?.department_id,
        assignedBy: user?.id
      });
      onClose();
    } catch (err) {
      console.error('Error assigning trainer:', err);
      setError(err?.message || 'Failed to assign trainer');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    try {
      setLoading(true);
      setError(null);
      await unitService.unassignTrainer(unit?.id);
      if (onSave) {
        // Just trigger refresh
        await onSave({ unitId: unit?.id, unassign: true });
      }
      onClose();
    } catch (err) {
      console.error('Error unassigning trainer:', err);
      setError(err?.message || 'Failed to unassign trainer');
    } finally {
      setLoading(false);
    }
  };

  // Check for cross-dept assignment
  const selectedTrainer = trainers.find(t => t.id === selectedTrainerId);
  const isCrossDept = selectedTrainer && unit?.program?.department?.id &&
    (selectedTrainer.department?.id || selectedTrainer.department_id) !== unit.program.department.id;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-xl font-bold text-foreground">
              {unit?.trainerAssignment?.trainer ? 'Reassign Trainer' : 'Assign Trainer'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {unit?.code} - {unit?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-error/10 border border-error rounded-lg p-4 mb-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-error">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-error hover:text-error/80"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Assignment */}
            {unit?.trainerAssignment?.trainer && (
              <div className="bg-muted rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-foreground mb-2">Current Assignment:</p>
                <div className="flex items-center space-x-2">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {unit?.trainerAssignment?.trainer?.fullName}
                  </span>
                </div>
                <div className="flex items-center space-x-2 mt-1">
                  <Icon name="Building" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {unit?.trainerAssignment?.trainer?.department?.name}
                  </span>
                </div>
              </div>
            )}

            {/* Trainer Selection */}
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-foreground">
                  Select Trainer <span className="text-error">*</span>
                </label>
                {!loadingTrainers && trainers.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {filteredTrainers.length} of {trainers.length} trainers
                  </span>
                )}
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                    if (selectedTrainerId) setSelectedTrainerId('');
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder={loadingTrainers ? "Loading trainers..." : "Search name, email or PF number..."}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary pl-9"
                  disabled={loadingTrainers}
                />
                <div className="absolute left-3 top-2.5 text-muted-foreground">
                  <Icon name="Search" size={16} />
                </div>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(open => !open)}
                  className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  aria-label={isDropdownOpen ? 'Hide trainer list' : 'Show trainer list'}
                >
                  <Icon name={isDropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
                </button>
              </div>

              {/* Kept in the modal layout so the window grows dynamically and the list cannot be clipped. */}
              {isDropdownOpen && !loadingTrainers && (
                <div className="mt-2 max-h-[42vh] overflow-y-auto rounded-lg border border-border bg-card shadow-inner">
                  {filteredTrainers.length > 0 ? (
                    filteredTrainers.map(trainer => (
                      <div
                        key={trainer.id}
                        className="px-4 py-3 hover:bg-muted cursor-pointer flex flex-col border-b last:border-b-0"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent focus loss
                          handleTrainerSelect(trainer);
                        }}
                      >
                        <span className="text-sm font-medium text-foreground">
                          {trainer.full_name || trainer.name || trainer.email}
                        </span>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-muted-foreground">
                            {trainer.employee_id || 'No PF'} · {trainer.department?.name || 'Department not assigned'}
                          </span>
                          {(trainer.max_workload_units || trainer.max_workload_hours) && (
                            <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              {trainer.max_workload_units ? `Units: ${trainer.current_units || 0}/${trainer.max_workload_units}` : ''}
                              {trainer.max_workload_units && trainer.max_workload_hours ? ' | ' : ''}
                              {trainer.max_workload_hours ? `Hrs: ${trainer.current_hours || 0}/${trainer.max_workload_hours}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      {searchTerm ? 'No trainers match your search.' : 'No active trainers are available.'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Info Note - Conditional */}
            {isCrossDept && (
              <div className="bg-warning/10 border border-warning/50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warning-foreground">
                    This trainer belongs to another department. Their mother-department HOD must approve the assignment within seven days.
                  </p>
                </div>
              </div>
            )}

            {/* Same Dept Info */}
            {!isCrossDept && selectedTrainerId && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Standard assignment within the same department.
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          {unit?.trainerAssignment?.trainer && (
            <Button
              variant="destructive"
              onClick={handleUnassign}
              disabled={loading}
              className="mr-auto"
            >
              {loading ? 'Unassigning...' : 'Unassign Trainer'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={loading || loadingTrainers}
          >
            {loading ? (
              <>
                <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Icon name="UserPlus" size={16} className="mr-2" />
                {unit?.trainerAssignment?.trainer ? 'Reassign Trainer' : 'Assign Trainer'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainerAssignmentModal;